import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import {
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   useToast,
   useColorMode,
   Spinner,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { getCompanyDetailsByGst } from '../../../../../redux/actions/company';
import { getTransport, addTransport, updateTransport } from '../../../../../redux/actions/transport';
import '../../../companyProfile/css.css';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { renderDynamicDropdown as renderDynamicUiDropdown } from 'components/ui/DynamicDropdown';
import { getShareCompanyOptions } from '../../../../../utils/util';
import { useGlobal } from 'contexts';
import { useUser } from 'contexts';

// GST regex pattern
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Yup Validation Schema
const validationSchema = Yup.object().shape({
   name: Yup.string().required('Name is required').min(1, 'Name must be at least 1 characters long'),
   mode: Yup.string().required('Mode is required'),
   gst: Yup.string()
      .nullable()
      .test(
         'gst-format',
         'Invalid GST number format',
         (value) => !value || GST_REGEX.test(value),
      ),
   email: Yup.string()
      .nullable()
      .test('email-format', 'Invalid email format', (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)),
   mobile: Yup.string()
      .nullable()
      .test('mobile-format', 'Mobile number must be 10 digits', (value) => !value || /^\d{10}$/.test(value)),
});

const TransportMasterForm = ({
   isOpen,
   onClose,
   isEditMode = false,
   demoname,
   currentRow = null,
   onSave,
   saveandcontinue = true,
}) => {
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';

   const [row, setRow] = useState({
      name: '',
      mode: null,
      gst: null,
      email: null,
      mobile: null,
      compIds: [],
   });
   const { user } = useUser();
   const [errors, setErrors] = useState({});
   const [isLoading, setIsLoading] = useState(false);

   // GST lookup state
   const [isFetchingGst, setIsFetchingGst] = useState(false);
   const [gstFetchStatus, setGstFetchStatus] = useState(null);
   const [gstError, setGstError] = useState(null);
   const gstDebounceRef = useRef(null);

   const toast = useToast();
   const dispatch = useDispatch();
   const { companyList, userGlobalSettingsData, company } = useGlobal();
   const globalSetting = userGlobalSettingsData?.[0]?.askCompany?.transport;
   const GstDetails = useSelector((state) => state.company.gstDetail);
   const shouldShowCompanyDropdown = globalSetting || false;
   const dbName = company?.dbName;

   const handleCompanyChange = useCallback((selectedOptions) => {
      setRow((prev) => ({
         ...prev,
         compIds: selectedOptions ?? [],
      }));
   }, []);

   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(
      () =>
         companyOptions.map((company) => ({
            value: company.value,
            label: company.label,
            id: company.value,
            name: company.label,
         })),
      [companyOptions],
   );

   const clearFormData = () => {
      setRow({
         name: '',
         mode: null,
         gst: null,
         email: null,
         mobile: null,
         compIds: [],
      });
      setErrors({});
      setGstFetchStatus(null);
      setGstError(null);
   };

   useEffect(() => {
      if (user?.loginhistory?.companyId && !isEditMode) {
         setRow((prev) => {
            if (prev.compIds.length === 0 || !prev.compIds.includes(user.loginhistory.companyId)) {
               return {
                  ...prev,
                  compIds: [user.loginhistory.companyId],
               };
            }
            return prev;
         });
      }
   }, [user?.loginhistory?.companyId, isEditMode]);

   useEffect(() => {
      if (!isEditMode) {
         clearFormData();
         setRow((prev) => ({
            ...prev,
            mode: prev.mode !== null ? prev.mode : 1,
            compIds: user?.loginhistory?.companyId ? [user.loginhistory.companyId] : [],
         }));
      } else if (isEditMode && currentRow) {
         setRow({
            name: currentRow.name || '',
            mode: currentRow.mode || null,
            gst: currentRow.gst || null,
            email: currentRow.email || null,
            mobile: currentRow.mobile || null,
            compIds: currentRow.compIds || [],
         });
      }
   }, [isEditMode, currentRow]);

   const lastFetchedGst = useRef(null);

   useEffect(() => {
      if (!isFetchingGst) return;
      if (!GstDetails) return;

      const details = GstDetails?.data ?? GstDetails;
      const filledName = details?.lgnm || details?.tradeNam;

      if (filledName) {
         setRow((prev) => ({ ...prev, name: filledName }));
         setGstFetchStatus('success');
         setIsFetchingGst(false);
      }
   }, [GstDetails]);

   const fetchAndFillGstDetails = useCallback(
      async (gstValue) => {
         if (!gstValue || !GST_REGEX.test(gstValue)) return;

         if (lastFetchedGst.current === gstValue) return;
         lastFetchedGst.current = gstValue;

         setIsFetchingGst(true);
         setGstFetchStatus(null);

         try {
            await dispatch(getCompanyDetailsByGst(gstValue));
         } catch (error) {
            setGstFetchStatus('error');
            setIsFetchingGst(false);
            console.error(error)
         }
      },
      [dispatch],
   );

   const handleGstChange = useCallback(
      (value) => {
         const upperValue = value ? value.toUpperCase() : value;

         setRow((prev) => ({ ...prev, gst: upperValue }));

         setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            if (newErrors.gst) delete newErrors.gst;
            return newErrors;
         });

         setGstFetchStatus(null);
         setGstError(null);

         if (gstDebounceRef.current) clearTimeout(gstDebounceRef.current);

         if (!upperValue) return;

         if (!GST_REGEX.test(upperValue)) {
            if (upperValue.length >= 15) {
               setGstError('Invalid GST number format');
            }
            lastFetchedGst.current = null;
            return;
         }

         gstDebounceRef.current = setTimeout(() => {
            fetchAndFillGstDetails(upperValue);
         }, 300);
      },
      [fetchAndFillGstDetails],
   );

   useEffect(() => {
      return () => {
         if (gstDebounceRef.current) clearTimeout(gstDebounceRef.current);
      };
   }, []);

   const validateStep = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
            return false;
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      setRow((prev) => ({
         ...prev,
         [field]: value,
      }));

      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const onFormSubmit = async () => {
      setIsLoading(true);
      const processedData = {
         mode: Number(row.mode),
         mobile: row.mobile ? row.mobile : undefined,
         email: row.email ? row.email : undefined,
         gst: row.gst ? row.gst : undefined,
         compIds: row.compIds ? row.compIds : undefined,
         name: row.name ? row.name : undefined,
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            processedData.id = currentRow.id;
            await dispatch(updateTransport(processedData));
         } else {
            const response = await dispatch(addTransport(processedData));
            if (response?.code === 1) {
               onSave(response.data);
            }
         }
         clearFormData();
         dispatch(getTransport());
         onClose();
         setIsLoading(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving the category.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         setIsLoading(false);
      }
   };

   const onsavecontinue = async () => {
      setIsLoading(true);
      const processedData = {
         mode: Number(row.mode),
         mobile: row.mobile ? row.mobile : undefined,
         email: row.email ? row.email : undefined,
         gst: row.gst ? row.gst : undefined,
         compIds: row.compIds ? row.compIds : undefined,
         name: row.name ? row.name : undefined,
      };

      try {
         if (isEditMode && currentRow && currentRow.id) {
            processedData.id = currentRow.id;
            await dispatch(updateTransport(processedData));
         } else {
            const response = await dispatch(addTransport(processedData));
            clearFormData();
            if (response?.code === 1) {
               onSave(response.data);
               onClose();
            }
         }
         dispatch(getTransport());
         setIsLoading(false);
      } catch (error) {
         toast({
            title: 'Error',
            description: error.message || 'An error occurred while saving the category.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
         setIsLoading(false);
      }
   };

   const handleSubmit = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onFormSubmit();
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handlesavecontinue = async () => {
      const isValid = await validateStep();
      if (isValid) {
         onsavecontinue();
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handleCansel = async () => {
      onClose();
      if (!isEditMode) {
         clearFormData();
      }
   };

   useEffect(() => {
      if (demoname && row.name === '') {
         handleInputChange('name', demoname);
      }
      demoname = '';
   }, [demoname, row.name]);

   const nameInputRef = useRef(null);
   const savefinishRef = useRef(null);
   const companyRef = useRef(null);
   const cancelRef = useRef(null);
   const savecontinueRef = useRef(null);
   const mobileRef = useRef(null);
   const emailRef = useRef(null);
   const modeRef = useRef(null);
   const gstRef = useRef(null);

   useEffect(() => {
      setTimeout(() => {
         nameInputRef.current?.focus();
      }, 100);
   }, [isOpen]);

   useEffect(() => {
      const handleKeyDown = (e) => {
         const activeElement = document.activeElement;
         const isTyping =
            activeElement?.tagName === 'INPUT' ||
            activeElement?.tagName === 'TEXTAREA' ||
            activeElement?.getAttribute('role') === 'textbox' ||
            activeElement?.isContentEditable;

         if (isTyping) return;

         if (isOpen && e.key.toLowerCase() === 's') {
            if (e.shiftKey) {
               handlesavecontinue();
            } else {
               handleSubmit();
            }
         }
         if (isOpen && e.key.toLowerCase() === 'c') {
            handleCansel();
         }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
         window.removeEventListener('keydown', handleKeyDown);
      };
   }, [validateStep]);

   useEffect(() => {
      const handleEsc = (e) => {
         if (e.key === 'Escape') {
            e.preventDefault();
            handleCansel();
         }
      };

      document.addEventListener('keydown', handleEsc);
      return () => {
         document.removeEventListener('keydown', handleEsc);
      };
   }, [handleCansel]);

   const GstStatusIcon = () => {
      if (isFetchingGst) {
         return (
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)' }}>
               <Spinner size="xs" color={isDark ? 'blue.300' : 'blue.500'} />
            </span>
         );
      }
      if (gstFetchStatus === 'success') {
         return (
            <span
               title="Details auto-filled"
               style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#10b981',
                  fontSize: 16,
                  lineHeight: 1,
               }}>
               ✓
            </span>
         );
      }
      if (gstFetchStatus === 'error') {
         return (
            <span
               title="Could not fetch details"
               style={{
                  position: 'absolute',
                  right: 10,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#ef4444',
                  fontSize: 16,
                  lineHeight: 1,
               }}>
               ✗
            </span>
         );
      }
      return null;
   };

   return (
      <Modal isOpen={isOpen} onClose={handleCansel} size="4xl">
         <ModalOverlay className="backdrop-blur-sm bg-black/30" />
         <ModalContent
            className={`
               ${isDark ? 'bg-gray-800' : 'bg-white'}
               rounded-2xl shadow-2xl mx-4
               ${isDark ? 'border border-gray-700' : 'border border-gray-200'}
            `}
            style={{ marginTop: window.innerWidth < 768 ? '20%' : window.innerWidth < 1280 ? '10%' : '8%' }}>
            {/* Header with gradient */}
            <ModalHeader
               className={`
                  text-xl font-semibold py-5 px-6 rounded-t-2xl
                  ${isDark
                     ? 'bg-gradient-to-r from-blue-900 to-indigo-900 text-white border-b border-gray-700'
                     : 'bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 border-b border-blue-200'
                  }
               `}>
               <div className="flex items-center gap-3">
                  <div
                     className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-800' : 'bg-blue-500'
                        }`}>
                     <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                           strokeLinecap="round"
                           strokeLinejoin="round"
                           strokeWidth={2}
                           d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                     </svg>
                  </div>
                  <span>{isEditMode ? 'Edit Transport' : 'Add New Transport'}</span>
               </div>
            </ModalHeader>

            <ModalCloseButton
               className={`${isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-800'} mt-2`}
            />

            <ModalBody className="p-6">
               {/* Transport Information Section */}
               <div className="mb-6">
                  <div
                     className={`
                     flex items-center gap-2 mb-4 pb-2 border-b-2
                     ${isDark ? 'border-indigo-700' : 'border-indigo-300'}
                  `}>
                     <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-indigo-900' : 'bg-indigo-100'
                           }`}>
                        <svg
                           className={`w-5 h-5 ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`}
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13 10V3L4 14h7v7l9-11h-7z"
                           />
                        </svg>
                     </div>
                     <h3 className={`text-lg font-semibold ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                        Transport Information
                     </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                     {DynamicInput(
                        'name',
                        'Name',
                        {
                           value: row.name,
                           onChange: (e) => handleInputChange('name', e.target.value),
                           ref: nameInputRef,
                           onKeyDown: (e) => {
                              if ((e.key === 'Enter' || e.key === 'Tab') && !e.shiftKey) {
                                 e.preventDefault();
                                 modeRef.current?.focus();
                              }
                           },
                        },
                        errors,
                        isDark ? '#9E9E9E' : '#4B5563',
                        'text',
                        true,
                     )}
                     {renderDynamicDropdown(
                        'mode',
                        'mode',
                        [
                           { id: 1, name: 'ROAD' },
                           { id: 2, name: 'RAIL' },
                           { id: 3, name: 'AIR' },
                           { id: 4, name: 'SHIP' },
                        ],
                        {
                           value: row.mode,
                           onChange: (value) => handleInputChange('mode', value),
                           ref: modeRef,
                           onKeyDown: (e) => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                 e.preventDefault();
                                 if (e.shiftKey) {
                                    nameInputRef.current?.focus();
                                 } else {
                                    gstRef.current?.focus();
                                 }
                              }
                           },
                        },
                        errors,
                        isDark ? '#9E9E9E' : '#4B5563',
                        false,
                        'Select Mode',
                     )}

                     {/* GST field wrapped in a relative container to show the status icon */}
                     <div style={{ position: 'relative' }}>
                        {DynamicInput(
                           'gst',
                           'Gst no',
                           {
                              value: row.gst,
                              // Use the new GST-aware handler
                              onChange: (e) => handleGstChange(e.target.value),
                              ref: gstRef,
                              onKeyDown: (e) => {
                                 if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    if (e.shiftKey) {
                                       modeRef.current?.focus();
                                    } else {
                                       emailRef.current?.focus();
                                    }
                                 }
                              },
                              // Nudge input text left so the icon doesn't overlap text
                              style: { paddingRight: '2rem' },
                           },
                           errors,
                           isDark ? '#9E9E9E' : '#4B5563',
                           'text',
                           false,
                        )}
                        <GstStatusIcon />
                        {gstError && (
                           <p style={{
                              color: '#ef4444',
                              fontSize: '0.75rem',
                              marginTop: '4px',
                              marginLeft: '2px',
                           }}>
                              {gstError}
                           </p>
                        )}
                     </div>
                  </div>
               </div>

               {/* Contact & Account Section */}
               <div>
                  <div
                     className={`
                     flex items-center gap-2 mb-4 pb-2 border-b-2
                     ${isDark ? 'border-emerald-700' : 'border-emerald-300'}
                  `}>
                     <div
                        className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-emerald-900' : 'bg-emerald-100'
                           }`}>
                        <svg
                           className={`w-5 h-5 ${isDark ? 'text-emerald-300' : 'text-emerald-600'}`}
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                           />
                        </svg>
                     </div>
                     <h3 className={`text-lg font-semibold ${isDark ? 'text-emerald-300' : 'text-emerald-700'}`}>
                        Contact & Account Details
                     </h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                     {DynamicInput(
                        'email',
                        'email',
                        {
                           value: row.email,
                           onChange: (e) => handleInputChange('email', e.target.value),
                           ref: emailRef,
                           onKeyDown: (e) => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                 e.preventDefault();
                                 if (e.shiftKey) {
                                    gstRef.current?.focus();
                                 } else {
                                    mobileRef.current?.focus();
                                 }
                              }
                           },
                        },
                        errors,
                        isDark ? '#9E9E9E' : '#4B5563',
                        'email',
                        false,
                     )}
                     {DynamicInput(
                        'mobile',
                        'mobile',
                        {
                           value: row.mobile,
                           onChange: (e) => handleInputChange('mobile', e.target.value),
                           ref: mobileRef,
                           onKeyDown: (e) => {
                              if (e.key === 'Enter' || e.key === 'Tab') {
                                 e.preventDefault();
                                 if (e.shiftKey) {
                                    emailRef.current?.focus();
                                 } else {
                                    if (shouldShowCompanyDropdown) {
                                       companyRef.current?.focus();
                                    } else {
                                       savefinishRef.current?.focus();
                                    }
                                 }
                              }
                           },
                        },
                        errors,
                        isDark ? '#9E9E9E' : '#4B5563',
                        'number',
                        false,
                     )}

                     {shouldShowCompanyDropdown && (
                        <div>
                           {renderDynamicUiDropdown(
                              'company',
                              'Company',
                              lastcompanyOptions,
                              {
                                 ref: companyRef,
                                 value: row.compIds,
                                 onChange: handleCompanyChange,
                                 onKeyDown: (e) => {
                                    if (e.key === 'Enter' || e.key === 'Tab') {
                                       e.preventDefault();
                                       if (e.shiftKey) {
                                          mobileRef.current?.focus();
                                       } else {
                                          savefinishRef.current?.focus();
                                       }
                                    }
                                 },
                              },
                              {},
                              isDark ? '#9E9E9E' : '#4B5563',
                              false,
                              'Select company',
                              true,
                              undefined,
                              true,
                           )}
                        </div>
                     )}
                  </div>
               </div>
            </ModalBody>

            <ModalFooter
               className={`
               p-6 rounded-b-2xl border-t
               ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'}
            `}>
               <div className="flex gap-3 flex-wrap justify-end w-full">
                  {!isEditMode && saveandcontinue === true && (
                     <Button
                        ref={savecontinueRef}
                        onClick={handlesavecontinue}
                        isLoading={isLoading}
                        disabled={isLoading}
                        className={`
                           px-6 py-2 rounded-lg font-medium transition-all duration-200
                           ${isDark
                              ? 'bg-indigo-600 hover:bg-indigo-700 text-white shadow-lg shadow-indigo-900/50'
                              : 'bg-indigo-500 hover:bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
                           }
                        `}
                        colorScheme="blue"
                        _focus={{ boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.4)' }}
                        onKeyDown={(e) => {
                           if (e.key === 'ArrowRight') {
                              e.preventDefault();
                              savefinishRef.current?.focus();
                           }
                        }}>
                        Save & Continue
                     </Button>
                  )}

                  <Button
                     onClick={handleSubmit}
                     isLoading={isLoading}
                     disabled={isLoading}
                     ref={savefinishRef}
                     className={`
                        px-6 py-2 rounded-lg font-medium transition-all duration-200
                        ${isDark
                           ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-900/50'
                           : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-lg shadow-emerald-500/30'
                        }
                     `}
                     colorScheme="green"
                     _focus={{ boxShadow: '0 0 0 3px rgba(16, 185, 129, 0.4)' }}
                     onKeyDown={(e) => {
                        if (e.key === 'ArrowRight') {
                           e.preventDefault();
                           cancelRef.current?.focus();
                        } else if (e.key === 'ArrowLeft') {
                           e.preventDefault();
                           if (!isEditMode && saveandcontinue === true) {
                              savecontinueRef.current?.focus();
                           }
                        }
                     }}>
                     Save & Finish
                  </Button>

                  <Button
                     ref={cancelRef}
                     variant="ghost"
                     onClick={handleCansel}
                     className={`
                        px-6 py-2 rounded-lg font-medium transition-all duration-200
                        ${isDark
                           ? 'text-gray-300 hover:bg-gray-700 hover:text-white'
                           : 'text-gray-700 hover:bg-gray-200 hover:text-gray-900'
                        }
                     `}
                     onKeyDown={(e) => {
                        if (e.key === 'ArrowLeft') {
                           e.preventDefault();
                           savefinishRef.current?.focus();
                        }
                     }}
                     _focus={{ boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.4)' }}>
                     Cancel
                  </Button>
               </div>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default TransportMasterForm;